import React, { useMemo } from "react";
import styled from "styled-components";
import { colors, causeColors } from "./colors";
import { lostOf, measureOf } from "./drops";
import { formatCompact, formatSignedPercent, pctChange } from "./format";
import { Part, PartTitle, PartNote } from "./Part";
import { Square } from "./CauseTag";

export default function CauseDeviceBreakdown({ drops, measure, devices, onCauseSelect }) {
    const causes = useMemo(() => {
        const totals = new Map();
        drops
            .filter((drop) => drop.entityType !== "Device")
            .forEach((drop) => totals.set(drop.cause, (totals.get(drop.cause) ?? 0) + lostOf(drop, measure)));
        return [...totals.entries()].map(([cause, lost]) => ({ cause, lost })).sort((a, b) => b.lost - a.lost);
    }, [drops, measure]);
    const total = causes.reduce((sum, cause) => sum + cause.lost, 0) || 1;
    const maxDevicePct = Math.max(...devices.map((device) => Math.abs(pctChange(device.before, device.after))), 1);

    return (
        <Part>
            <PartTitle>Lost {measureOf(measure).unit} by likely cause</PartTitle>
            <PartNote>Page, section, query & country drops</PartNote>
            <Stack aria-hidden>
                {causes.map((cause) => <span key={cause.cause} style={{ width: `${(cause.lost / total) * 100}%`, background: causeColors[cause.cause] }} />)}
            </Stack>
            <Causes>
                {causes.map((cause) => (
                    <li key={cause.cause}>
                        <CauseButton type="button" onClick={() => onCauseSelect(cause.cause)}>
                            <Square $color={causeColors[cause.cause]} />
                            <Name>{cause.cause}</Name>
                            <Share>{Math.round((cause.lost / total) * 100)}%</Share>
                            <Lost>−{formatCompact(cause.lost)}</Lost>
                        </CauseButton>
                    </li>
                ))}
            </Causes>

            <DeviceTitle>Change by device</DeviceTitle>
            <Devices>
                {devices.map((device) => {
                    const change = pctChange(device.before, device.after);
                    const bad = change <= -15;
                    return (
                        <Device key={device.device}>
                            <span>{device.device}</span>
                            <Track><span style={{ width: `${(Math.abs(change) / maxDevicePct) * 100}%`, background: bad ? colors.critical : colors.subtle }} /></Track>
                            <DeviceChange $bad={bad}>{formatSignedPercent(change, 0)}</DeviceChange>
                        </Device>
                    );
                })}
            </Devices>
        </Part>
    );
}

const Stack = styled.div`
    display: flex;
    height: 10px;
    overflow: hidden;
    margin-top: 16px;
    border-radius: 999px;
    span { height: 100%; border-right: 2px solid ${colors.surface}; }
    span:last-child { border-right: none; }
`;

const Causes = styled.ul`
    margin: 12px 0 0;
    padding: 0;
    list-style: none;
`;

const CauseButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    width: calc(100% + 16px);
    margin: 0 -8px 2px;
    padding: 6px 8px;
    border: none;
    border-radius: 6px;
    background: none;
    text-align: left;
    font-size: 13px;
    cursor: pointer;
    &:hover { background: ${colors.canvas}; }
`;

const Name = styled.span`
    flex: 1;
    color: ${colors.ink};
`;

const Share = styled.span`
    font-variant-numeric: tabular-nums;
    color: ${colors.muted};
`;

const Lost = styled.span`
    width: 56px;
    text-align: right;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    color: ${colors.ink};
`;

const DeviceTitle = styled.h4`
    margin: 20px 0 0;
    padding-top: 16px;
    border-top: 1px solid ${colors.line};
    font-size: 12px;
    font-weight: 600;
    color: ${colors.ink};
`;

const Devices = styled.ul`
    margin: 8px 0 0;
    padding: 0;
    list-style: none;
`;

const Device = styled.li`
    display: grid;
    grid-template-columns: 64px minmax(0, 1fr) 56px;
    align-items: center;
    gap: 12px;
    margin-top: 8px;
    font-size: 13px;
    color: ${colors.ink};
`;

const Track = styled.span`
    height: 6px;
    overflow: hidden;
    border-radius: 999px;
    background: ${colors.line}b3;
    span { display: block; height: 100%; border-radius: 999px; }
`;

const DeviceChange = styled.span`
    text-align: right;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    color: ${({ $bad }) => ($bad ? colors.criticalInk : colors.muted)};
`;
